import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../internal/cx';
import { FOCUS_RING } from '../internal/classes';
import { Icon, type IconName } from './Icon';

/** Handle of a toast, returned by every `notify` call and accepted by `notify.dismiss`. */
export type ToastId = string | number;

/** A button inside a toast — "Geri al", "Yeniden dene", "Göster". */
export interface ToastAction {
  /** Label, in the reader's language. */
  label: string;
  /** What the button does. The toast closes itself afterwards. */
  onClick: () => void;
}

/** Everything a toast can carry besides its message. */
export interface ToastOptions {
  /** Second line with the detail; the message stays the headline. */
  description?: string;
  /** One button, usually an undo. */
  action?: ToastAction;
  /** How long the toast stays, in ms. The `<Toaster>` default applies when absent. */
  durationMs?: number;
  /** Own id, so a repeated event replaces its toast instead of stacking a second one. */
  id?: ToastId;
}

/** Where the stack of toasts sits. */
export type ToastPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

/** How a toast reads: neutral, or one of the three tones with an icon. */
type ToastTone = 'neutral' | 'success' | 'error' | 'info';

interface ToastRecord extends ToastOptions {
  readonly id: ToastId;
  readonly tone: ToastTone;
  readonly message: string;
}

/** The imperative toast API. */
export interface NotifyApi {
  /** Shows a neutral toast. */
  (message: string, options?: ToastOptions): ToastId;
  /** Shows a toast that confirms something worked. */
  success: (message: string, options?: ToastOptions) => ToastId;
  /** Shows a toast that reports a failure. */
  error: (message: string, options?: ToastOptions) => ToastId;
  /** Shows a toast that states a fact worth noticing. */
  info: (message: string, options?: ToastOptions) => ToastId;
  /** Closes one toast, or every toast when no id is given. */
  dismiss: (id?: ToastId) => void;
}

/**
 * The toasts currently on screen. A plain store rather than a React context:
 * `notify` is called from use cases and event handlers that have no component
 * around them, and the implementation stays ours — no library injects a
 * stylesheet at import time, which a strict CSP would refuse (ADR 0007 §8).
 */
const listeners = new Set<() => void>();
let toasts: readonly ToastRecord[] = [];
let sequence = 0;

function publish(next: readonly ToastRecord[]): void {
  toasts = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function show(tone: ToastTone, message: string, options: ToastOptions = {}): ToastId {
  const id = options.id ?? `toast-${++sequence}`;
  const record: ToastRecord = { ...options, id, tone, message };
  // A repeated event replaces its own toast instead of stacking another one.
  const without = toasts.filter((toast) => toast.id !== id);
  publish([...without, record]);
  return id;
}

function dismiss(id?: ToastId): void {
  publish(id === undefined ? [] : toasts.filter((toast) => toast.id !== id));
}

/**
 * Shows a toast from anywhere — a use case, an event handler, a retry — without a React context.
 * A `<Toaster>` has to be mounted for the toast to appear.
 *
 * @example notify.success(t('mail.archived'), { action: { label: t('common.undo'), onClick: undo } })
 */
export const notify: NotifyApi = Object.assign(
  (message: string, options?: ToastOptions): ToastId => show('neutral', message, options),
  {
    success: (message: string, options?: ToastOptions): ToastId => show('success', message, options),
    error: (message: string, options?: ToastOptions): ToastId => show('error', message, options),
    info: (message: string, options?: ToastOptions): ToastId => show('info', message, options),
    dismiss,
  },
);

/** How long a toast stays by default, in ms. Long enough to read a sentence and press Undo. */
const DEFAULT_DURATION_MS = 6000;
/** How many toasts are stacked before the oldest is dropped. */
const DEFAULT_MAX_VISIBLE = 3;

const TONE_ICON: Record<Exclude<ToastTone, 'neutral'>, { name: IconName; className: string }> = {
  success: { name: 'success', className: 'text-success' },
  error: { name: 'error', className: 'text-(--ui-danger-strong)' },
  info: { name: 'info', className: 'text-info' },
};

const POSITION_CLASS: Record<ToastPosition, string> = {
  'top-left': 'top-3 left-3 items-start',
  'top-center': 'top-3 left-1/2 -translate-x-1/2 items-center',
  'top-right': 'top-3 right-3 items-end',
  'bottom-left': 'bottom-3 left-3 items-start',
  'bottom-center': 'bottom-3 left-1/2 -translate-x-1/2 items-center',
  'bottom-right': 'bottom-3 right-3 items-end',
};

/** Props of {@link Toaster}. */
export interface ToasterProps {
  /** Accessible name of the region holding the toasts, e.g. "Bildirimler". */
  label: string;
  /** Accessible name of each toast's close button, e.g. "Kapat". */
  closeLabel: string;
  /** Where the stack sits; `bottom-right` by default. */
  position?: ToastPosition;
  /** Default lifetime of a toast, in ms; 6000 by default. */
  durationMs?: number;
  /** How many toasts are stacked at once; 3 by default. */
  maxVisible?: number;
}

/**
 * The region the toasts appear in. Mount it once, at the application root. Hovering or focusing the
 * stack pauses the timers, so a reader who needs longer keeps the message (WCAG 2.2 AA, 2.2.1).
 *
 * @example <Toaster label={t('common.notifications')} closeLabel={t('common.close')} />
 */
export function Toaster({
  label,
  closeLabel,
  position = 'bottom-right',
  durationMs = DEFAULT_DURATION_MS,
  maxVisible = DEFAULT_MAX_VISIBLE,
}: ToasterProps) {
  const current = useSyncExternalStore(subscribe, () => toasts, () => toasts);
  const [paused, setPaused] = useState(false);
  const visible = current.slice(-maxVisible);

  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      role="region"
      aria-label={label}
      className={cx('pointer-events-none fixed z-50 flex w-[22rem] max-w-[calc(100vw-1.5rem)] flex-col gap-2', POSITION_CLASS[position])}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {visible.map((toast) => (
        <ToastItem key={toast.id} toast={toast} closeLabel={closeLabel} defaultDurationMs={durationMs} paused={paused} />
      ))}
    </div>,
    document.body,
  );
}

interface ToastItemProps {
  toast: ToastRecord;
  closeLabel: string;
  defaultDurationMs: number;
  paused: boolean;
}

function ToastItem({ toast, closeLabel, defaultDurationMs, paused }: ToastItemProps) {
  const lifetime = toast.durationMs ?? defaultDurationMs;
  const remaining = useRef(lifetime);
  /** When the current countdown started; 0 until the first tick, so render stays pure. */
  const startedAt = useRef(0);

  useEffect(() => {
    if (paused) {
      if (startedAt.current > 0) remaining.current = Math.max(0, remaining.current - (Date.now() - startedAt.current));
      return;
    }
    startedAt.current = Date.now();
    const timer = setTimeout(() => dismiss(toast.id), remaining.current);
    return () => clearTimeout(timer);
  }, [paused, toast.id]);

  const runAction = useCallback(() => {
    toast.action?.onClick();
    dismiss(toast.id);
  }, [toast.action, toast.id]);

  const icon = toast.tone === 'neutral' ? null : TONE_ICON[toast.tone];
  return (
    <div
      // An error interrupts; anything else waits for a pause in what the reader is doing.
      role={toast.tone === 'error' ? 'alert' : 'status'}
      className={cx(
        'pointer-events-auto relative flex w-full items-start gap-2 rounded-md border border-border',
        'bg-surface-elevated p-3 text-text shadow-md',
      )}
    >
      {icon && <Icon name={icon.name} className={cx('mt-0.5 shrink-0', icon.className)} />}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-subhead-strong text-text">{toast.message}</span>
        {toast.description && <span className="text-caption text-text-secondary">{toast.description}</span>}
      </div>
      {toast.action && (
        <button
          type="button"
          onClick={runAction}
          className={cx('ml-2 shrink-0 cursor-pointer self-center rounded-sm bg-brand px-2 py-1', 'text-caption-strong text-text-on-brand', FOCUS_RING)}
        >
          {toast.action.label}
        </button>
      )}
      <button
        type="button"
        aria-label={closeLabel}
        onClick={() => dismiss(toast.id)}
        className={cx(
          'absolute -top-2 -left-2 flex size-5 cursor-pointer items-center justify-center',
          'rounded-pill border border-border bg-surface-elevated text-text-secondary',
          FOCUS_RING,
        )}
      >
        <Icon name="close" size="sm" />
      </button>
    </div>
  );
}
